import { fetchEventSource, EventStreamContentType } from '@microsoft/fetch-event-source';
import { useAuthStore } from '@/state/store/auth';
import { getGuestBlocksTokenSync } from '@/state/store/guestSession';

const BLOCKS_AI_URL = `${(import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')}/blocksai-api/v1/ai-agent/query/stream`;

// Resolve a Bearer token: prefer the authenticated user's JWT; otherwise fall
// back to the guest Blocks token. Returns null if neither is provisioned.
function resolveBearer(): string | null {
  const auth = useAuthStore.getState();
  if (!auth.isAuthenticated) return getGuestBlocksTokenSync();
  return null;
}

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

const LAST_N_TURN = 2;
const RESPONSE_TYPE = 'json';
const CALL_FROM = import.meta.env.VITE_PROJECT_SLUG;
// const MODEL_PROVIDER = 'azure';
// export const CONTRACT_GENERATION_MODEL = 'gpt-4o-mini';
// export const CONTRACT_GENERATION_MODEL = 'claude-sonnet-4-6';
export const CONTRACT_GENERATION_MODEL_ID = 'anthropic/claude-sonnet-4.5';
const RESPONSE_FORMAT = {
  metadata: {
    title: 'string',
    effectiveDate: 'string',
    documentType: 'string',
    jurisdiction: 'string',
    generatedAt: 'string',
  },
  content: [
    {
      type: 'string',
      props: { title: 'string', number: 'string' },
      children: [
        {
          type: 'string',
          props: { title: 'string', number: 'string' },
          children: [{ type: 'string', text: 'string' }],
        },
      ],
    },
  ],
  signatories: [
    {
      party: 'string',
      name: 'string',
      email: 'string',
      title: 'string',
    },
  ],
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BlocksAiRequest {
  query: string;
  basePrompt?: string;
  modelName?: string;
  modelId?: string;
  signal?: AbortSignal;
  onMessage?: (chunk: string) => void;
}

export interface BlocksAiResponse {
  message: string;
  sessionId?: string;
  messageId?: string;
  nextStepQuestions?: string[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

class FatalSSEError extends Error {}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryable(error: unknown): boolean {
  const s = (error instanceof Error ? error.message : String(error)).toLowerCase();
  return (
    s.includes('json') ||
    s.includes('parse') ||
    s.includes('timeout') ||
    s.includes('timed out') ||
    s.includes('rate limit') ||
    s.includes('429') ||
    s.includes('network') ||
    s.includes('fetch') ||
    s.includes('failed') ||
    s.includes('empty') ||
    s.includes('no content')
  );
}

// ---------------------------------------------------------------------------
// Core SSE call
// ---------------------------------------------------------------------------

async function callBlocksAi(request: BlocksAiRequest): Promise<BlocksAiResponse> {
  const { query, basePrompt, signal, onMessage } = request;

  const accessToken = resolveBearer();
  const isAuthenticated = useAuthStore.getState().isAuthenticated;
  if (!accessToken && !isAuthenticated) throw new Error('No access token available (auth or guest)');

  const body: Record<string, unknown> = {
    query,
    last_n_turn: LAST_N_TURN,
    response_type: RESPONSE_TYPE,
    response_format: JSON.stringify(RESPONSE_FORMAT),
    call_from: CALL_FROM,
    // model_provider: MODEL_PROVIDER,
    model_id: CONTRACT_GENERATION_MODEL_ID,
    // model_name: CONTRACT_GENERATION_MODEL,
  };

  if (basePrompt !== undefined) body.base_prompt = basePrompt;

  let responseMessage = '';
  let sessionIdResult: string | undefined;
  let messageId: string | undefined;
  let nextStepQuestions: string[] | undefined;

  await fetchEventSource(BLOCKS_AI_URL, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      'x-blocks-key': import.meta.env.VITE_X_BLOCKS_KEY,
    },
    body: JSON.stringify(body),
    signal,
    openWhenHidden: true,

    async onopen(response) {
      if (response.ok && response.headers.get('content-type')?.includes(EventStreamContentType)) {
        return;
      }
      if (response.status === 401 || response.status === 403) {
        // An authenticated user's token is dead and could not be refreshed —
        // clear auth state so the app redirects to /login with the expired
        // banner. Guest sessions don't own a refresh token, so leave them.
        if (useAuthStore.getState().isAuthenticated) {
          useAuthStore.getState().expireSession();
        }
        throw new FatalSSEError(`Authentication failed (status ${response.status})`);
      }
      const text = await response.text().catch(() => '');
      throw new Error(`Blocks AI request failed (status ${response.status}): ${text}`);
    },

    onmessage(event) {
      if (!event.data) return;

      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        return;
      }

      if (parsed.error) throw new Error(`Blocks AI error: ${parsed.error}`);

      // chat_response is the canonical final answer; its `message` may be a string OR an object.
      // task_complete carries an intermediate (sometimes truncated) buffer — use only as fallback.
      // All other events (start, context, workflow_*, task_progress) are status noise.
      if (event.event === 'chat_response') {
        const m = parsed.message;
        if (typeof m === 'string' && m) {
          responseMessage = m;
          onMessage?.(m);
        } else if (m && typeof m === 'object') {
          const asString = JSON.stringify(m);
          responseMessage = asString;
          onMessage?.(asString);
        }
        sessionIdResult = (parsed.session_id as string) ?? sessionIdResult;
        nextStepQuestions = (parsed.next_step_questions as string[]) ?? nextStepQuestions;
      } else if (event.event === 'task_complete') {
        const m = parsed.message;
        if (!responseMessage && typeof m === 'string' && m) {
          responseMessage = m;
        }
      } else if (event.event === 'message_end') {
        if (typeof parsed.message_id === 'string') {
          messageId = parsed.message_id;
        }
      }
    },

    onclose() {
      // stream ended
    },

    onerror(err) {
      throw err;
    },
  });

  if (!responseMessage) throw new Error('Blocks AI returned an empty response');

  return { message: responseMessage, sessionId: sessionIdResult, messageId, nextStepQuestions };
}

// ---------------------------------------------------------------------------
// Public API — with retry (exponential backoff)
// ---------------------------------------------------------------------------

export async function queryBlocksAi(request: BlocksAiRequest): Promise<BlocksAiResponse> {
  for (let attempt = 1; attempt <= MAX_RETRIES + 1; attempt++) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(
        `[BlocksAI] Attempt ${attempt}/${MAX_RETRIES + 1}${request.modelName ? ` (model: ${request.modelName})` : ''}`
      );
    }

    try {
      return await callBlocksAi(request);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      if (error instanceof FatalSSEError) throw error;

      if (import.meta.env.DEV) {
        console.error(`[BlocksAI] Attempt ${attempt} failed:`, error);
      }

      if (isRetryable(error) && attempt <= MAX_RETRIES) {
        await sleep(RETRY_DELAY_MS * Math.pow(2, attempt - 1));
        continue;
      }

      throw error;
    }
  }

  throw new Error('All retry attempts exhausted');
}
